import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// ── Manifest: route path → audio file. Fallback used for anything not listed.
// All files live at /app/frontend/public/audio/.
const TRACKS = {
  "/":            "/audio/main_theme.mp3",
  "/pilot":       "/audio/main_theme.mp3",
  "/nations":     "/audio/main_theme.mp3",
  "/characters":  "/audio/main_theme.mp3",
  "/gallery":     "/audio/main_theme.mp3",
  "/nation-quiz": "/audio/main_theme.mp3",
  "/archive":     "/audio/main_theme.mp3",
  "/archive/drop":"/audio/main_theme.mp3",
  "/anime-nyc":   "/audio/main_theme.mp3",
  "/community":   "/audio/main_theme.mp3",
  "/mission":     "/audio/main_theme.mp3",
  "/leaderboard": "/audio/main_theme.mp3",
  "/me/cast":     "/audio/main_theme.mp3",
};
const FALLBACK = "/audio/main_theme.mp3";

const CROSSFADE_MS = 800;
const LS_ENABLED = "cbb-audio-enabled";
const LS_VOLUME  = "cbb-audio-volume";
const DEFAULT_VOLUME = 0.35;

// Resolve the track file for a given pathname (exact match, fallback last).
function resolveTrack(pathname) {
  if (TRACKS[pathname]) return TRACKS[pathname];
  // Match longest prefix under /archive, /me, /keeper etc.
  const hit = Object.keys(TRACKS)
    .filter((p) => p !== "/" && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  return hit ? TRACKS[hit] : FALLBACK;
}

const AudioCtx = createContext(null);

export function AudioProvider({ children }) {
  const location = useLocation();
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_ENABLED) === "1"; } catch { return false; }
  });
  const [volume, setVolumeState] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem(LS_VOLUME));
      return Number.isFinite(v) && v >= 0 && v <= 1 ? v : DEFAULT_VOLUME;
    } catch { return DEFAULT_VOLUME; }
  });
  // Two <audio> elements for gapless crossfade.
  const aRef = useRef(null);
  const bRef = useRef(null);
  const activeRef = useRef("a"); // which element is currently the audible one
  const currentSrcRef = useRef(""); // src attached to the active element

  // Prepare audio elements on mount.
  useEffect(() => {
    if (!aRef.current) aRef.current = new Audio();
    if (!bRef.current) bRef.current = new Audio();
    for (const el of [aRef.current, bRef.current]) {
      el.loop = true;
      el.preload = "auto";
      el.volume = 0;
    }
    // Cleanup on unmount
    return () => {
      try { aRef.current?.pause(); bRef.current?.pause(); } catch { /* noop */ }
    };
  }, []);

  // Persist enabled + volume.
  useEffect(() => {
    try { localStorage.setItem(LS_ENABLED, enabled ? "1" : "0"); } catch { /* noop */ }
  }, [enabled]);
  useEffect(() => {
    try { localStorage.setItem(LS_VOLUME, String(volume)); } catch { /* noop */ }
  }, [volume]);

  const target = useMemo(() => resolveTrack(location.pathname), [location.pathname]);

  // Crossfade to the target track whenever route or enabled state changes.
  useEffect(() => {
    if (!aRef.current || !bRef.current) return;
    const active = activeRef.current === "a" ? aRef.current : bRef.current;
    const inactive = activeRef.current === "a" ? bRef.current : aRef.current;

    if (!enabled) {
      // Fade both out
      fade(active, active.volume, 0, CROSSFADE_MS, () => { try { active.pause(); } catch { /* noop */ } });
      fade(inactive, inactive.volume, 0, CROSSFADE_MS, () => { try { inactive.pause(); } catch { /* noop */ } });
      return;
    }

    // Same track and already playing → just ensure it's playing at correct volume.
    if (currentSrcRef.current === target && !active.paused) {
      fade(active, active.volume, volume, 300);
      return;
    }

    // Switch tracks with crossfade.
    inactive.src = target;
    inactive.currentTime = 0;
    inactive.volume = 0;
    const playPromise = inactive.play();
    if (playPromise && typeof playPromise.catch === "function") {
      // Autoplay blocked (rare — this only fires after user has toggled enabled, so should succeed)
      playPromise.catch(() => { setEnabled(false); });
    }
    fade(inactive, 0, volume, CROSSFADE_MS);
    fade(active,   active.volume, 0, CROSSFADE_MS, () => { try { active.pause(); } catch { /* noop */ } });
    activeRef.current = activeRef.current === "a" ? "b" : "a";
    currentSrcRef.current = target;
  }, [enabled, target, volume]);

  // Match live volume changes on active element (in addition to on switch).
  useEffect(() => {
    const active = activeRef.current === "a" ? aRef.current : bRef.current;
    if (active && enabled && !active.paused) active.volume = volume;
  }, [volume, enabled]);

  // Auto-mute + PAUSE when tab hidden / user leaves the site; resume when visible (if enabled).
  useEffect(() => {
    const active = () => (activeRef.current === "a" ? aRef.current : bRef.current);

    const hardStop = () => {
      const el = active();
      if (!el) return;
      try { el.pause(); el.volume = 0; } catch { /* noop */ }
    };

    const onVis = () => {
      const el = active();
      if (!el) return;
      if (document.hidden) {
        hardStop();
      } else if (enabled) {
        el.volume = 0;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
        fade(el, 0, volume, 400);
      }
    };
    // Extra belt-and-suspenders: hard pause when the window/tab is unloading or backgrounded on mobile.
    const onPageHide = () => hardStop();
    const onBlur     = () => hardStop();

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      window.removeEventListener("blur", onBlur);
    };
  }, [enabled, volume]);

  const value = useMemo(() => ({
    enabled,
    volume,
    toggle: () => setEnabled((v) => !v),
    setVolume: (v) => setVolumeState(Math.max(0, Math.min(1, v))),
  }), [enabled, volume]);

  return <AudioCtx.Provider value={value}>{children}</AudioCtx.Provider>;
}

export function useAudio() {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used inside AudioProvider");
  return ctx;
}

// ── Tiny volume ramp helper. Uses rAF so it stops if element pauses/reassigns.
function fade(el, from, to, ms, onDone) {
  if (!el) return;
  const start = performance.now();
  const clampedFrom = Math.max(0, Math.min(1, from));
  const clampedTo   = Math.max(0, Math.min(1, to));
  function step(now) {
    const t = Math.min(1, (now - start) / ms);
    try { el.volume = clampedFrom + (clampedTo - clampedFrom) * t; } catch { /* noop */ }
    if (t < 1) requestAnimationFrame(step);
    else if (onDone) onDone();
  }
  requestAnimationFrame(step);
}
